// src/components/Clients.js
// Clients B2C — /clients. The end customers who write in (the CRM): who they
// are, how to reach them, whose address needs a check. The API pages the
// list (skip / limit, newest first) and returns no total, so every number
// here is about the customers shown, and says so. « À vérifier » lists the
// addresses the voice agent could not verify. Search is server-side (name,
// e-mail or phone); the VIP and address filters apply to the page shown.
// URL state: ?q, ?page, ?size, ?vip=1, ?filter=address.
// API: GET /api/clients, POST /api/clients/sync.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight, CloudDownload, Crown, MapPin, Phone, RefreshCw, Search, TriangleAlert, UsersRound,
} from 'lucide-react';
import {
  Badge, Banner, Card, EmptyState, Kpi, PageHeader, Segmented, Toast,
} from '../design/ui';
import {
  addressNeedsReview, addressText, b2cPageSummary, matchesB2CFilter, sourceLabel,
} from './clientsB2C';
import { formatCount, formatShortDay, formatStamp, nextParams } from './clientsShared';
import './Clients.css';

const PAGE_SIZES = [25, 50, 100];
const SYNC_LIMIT = 1500;

const Clients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQuery = searchParams.get('q') || '';
  const sizeParam = Number(searchParams.get('size'));
  const pageSize = PAGE_SIZES.includes(sizeParam) ? sizeParam : 50;
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const filter = searchParams.get('vip') === '1' ? 'vip' : searchParams.get('filter') === 'address' ? 'address' : 'all';

  const updateParams = useCallback((updates) => {
    setSearchParams((previous) => nextParams(previous, updates), { replace: true });
  }, [setSearchParams]);

  const setFilter = (next) => updateParams({ vip: next === 'vip' ? '1' : null, filter: next === 'address' ? 'address' : null });

  // Debounced search input → URL
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchInput.trim() !== searchQuery) updateParams({ q: searchInput.trim(), page: null });
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const [clients, setClients] = useState(null); // null = loading
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(timer);
  }, [toast]);

  const fetchClients = useCallback(async ({ pageArg, limit, searchArg }) => {
    setClients(null);
    setError(null);
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    try {
      const response = await axios.get('/api/clients', {
        headers: { Authorization: `Bearer ${token}` },
        params: { skip: (pageArg - 1) * limit, limit, search: searchArg || undefined },
      });
      const payload = Array.isArray(response.data) ? response.data : [];
      setClients(payload);
      setHasMore(payload.length === limit);
    } catch (err) {
      setError(err.response?.data?.detail || t('clientsPage.fetchError', 'Impossible de charger les clients.'));
      setClients([]);
      setHasMore(false);
    }
  }, [navigate, t]);

  const reload = useCallback(
    () => fetchClients({ pageArg: page, limit: pageSize, searchArg: searchQuery }),
    [fetchClients, page, pageSize, searchQuery],
  );

  useEffect(() => { reload(); }, [reload]);

  const handleSync = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { navigate('/'); return; }
    setIsSyncing(true);
    setToast(null);
    try {
      const response = await axios.post('/api/clients/sync', null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: SYNC_LIMIT },
      });
      const data = response.data || {};
      setToast({
        tone: 'success',
        text: t('clientsPage.syncResult', '{{processed}} tickets traités · {{created}} clients créés · {{skipped}} ignorés', {
          processed: data.processed ?? 0,
          created: data.created ?? 0,
          skipped: data.skipped ?? 0,
        }),
      });
      if (page !== 1) updateParams({ page: null });
      else fetchClients({ pageArg: 1, limit: pageSize, searchArg: searchQuery });
    } catch (err) {
      setToast({ tone: 'danger', text: err.response?.data?.detail || t('clientsPage.syncError', 'Échec de la synchronisation des clients.') });
    } finally {
      setIsSyncing(false);
    }
  };

  const loading = clients === null;
  const list = useMemo(() => clients || [], [clients]);
  const summary = useMemo(() => b2cPageSummary(list), [list]);
  const displayed = useMemo(() => list.filter((client) => matchesB2CFilter(client, filter)), [list, filter]);

  const openClient = (client) => navigate(`/clients/${client.id}`, { state: { client } });

  const subtitle = loading
    ? t('clientsUi.b2c.subtitleLoading', 'Les particuliers qui écrivent à votre service client.')
    : t('clientsUi.b2c.subtitle', 'Les particuliers qui écrivent à votre service client · page {{page}}, {{count}} affichés, les plus récents d’abord', {
      page, count: formatCount(summary.shown),
    });

  const renderAttention = () => {
    if (loading || !summary.needsReview) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('clientsUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">1</Badge>}
        flush
        className="customers-v2__attention"
      >
        <ul className="u-list">
          <li className="u-row">
            <Badge tone="warning" dot>{t('clientsUi.attention.watch', 'À surveiller')}</Badge>
            <div className="u-row__main">
              <div className="u-row__title">
                {t('clientsUi.b2c.attnAddress', '{{count}} adresses à vérifier parmi les clients affichés', { count: summary.needsReview })}
              </div>
              <div className="u-row__meta">
                {t('clientsUi.b2c.attnAddressDetail', 'L’agent vocal n’a pas pu les vérifier : corrigez-les sur la fiche du client avant un envoi.')}
              </div>
            </div>
            <div className="u-row__side">
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setFilter('address')}>
                {t('clientsUi.see', 'Voir')}
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          </li>
        </ul>
      </Card>
    );
  };

  const renderKpis = () => {
    if (loading) {
      return (
        <div className="u-kpi-row" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton customers-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!summary.shown) return null;
    return (
      <div className="u-kpi-row">
        <Kpi
          icon={UsersRound}
          label={t('clientsUi.b2c.kpiShown', 'Clients affichés')}
          value={formatCount(summary.shown)}
          context={hasMore
            ? t('clientsUi.b2c.kpiShownMore', 'page {{page}} · d’autres pages suivent', { page })
            : t('clientsUi.b2c.kpiShownLast', 'page {{page}} · dernière page', { page })}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <Kpi
          icon={Crown}
          tone={summary.vip ? 'warning' : 'neutral'}
          label={t('clientsUi.b2c.kpiVip', 'VIP')}
          value={formatCount(summary.vip)}
          unit={`/ ${formatCount(summary.shown)}`}
          context={t('clientsUi.b2c.kpiVipContext', 'parmi les clients affichés')}
          meter={{ value: summary.vip, max: summary.shown, tone: 'warning', label: t('clientsUi.b2c.kpiVip', 'VIP') }}
          active={filter === 'vip'}
          onClick={() => setFilter('vip')}
        />
        <Kpi
          icon={Phone}
          tone="neutral"
          label={t('clientsUi.b2c.kpiPhone', 'Avec téléphone')}
          value={formatCount(summary.withPhone)}
          unit={`/ ${formatCount(summary.shown)}`}
          context={t('clientsUi.b2c.kpiPhoneContext', 'joignables par SMS ou appel')}
          meter={{ value: summary.withPhone, max: summary.shown, label: t('clientsUi.b2c.kpiPhone', 'Avec téléphone') }}
        />
        <Kpi
          icon={MapPin}
          tone={summary.needsReview ? 'warning' : 'success'}
          label={t('clientsUi.b2c.kpiAddress', 'Adresses à vérifier')}
          value={formatCount(summary.needsReview)}
          context={t('clientsUi.b2c.kpiAddressContext', 'non vérifiées par l’agent vocal')}
          contextTone={summary.needsReview ? 'warning' : undefined}
          active={filter === 'address'}
          onClick={() => setFilter('address')}
        />
      </div>
    );
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="customers-v2__skeleton" aria-busy="true">
          {Array.from({ length: 8 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    }
    if (!displayed.length) {
      if (error && !list.length) return null;
      if (filter !== 'all' && list.length) {
        return (
          <EmptyState
            icon={Search}
            title={filter === 'vip'
              ? t('clientsPage.emptyVip', 'Aucun client VIP sur cette page.')
              : t('clientsUi.b2c.emptyAddress', 'Aucune adresse à vérifier sur cette page.')}
            action={(
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setFilter('all')}>
                {t('clientsUi.clearFilter', 'Retirer le filtre')}
              </button>
            )}
          >
            {t('clientsUi.b2c.filterScope', 'Le filtre s’applique à la page affichée.')}
          </EmptyState>
        );
      }
      if (searchQuery) {
        return (
          <EmptyState
            icon={Search}
            title={t('clientsPage.emptySearch', 'Aucun client ne correspond à la recherche.')}
            action={(
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => { setSearchInput(''); updateParams({ q: null, page: null }); }}>
                {t('clientsUi.clearSearch', 'Effacer la recherche')}
              </button>
            )}
          />
        );
      }
      return (
        <EmptyState
          icon={UsersRound}
          title={t('clientsUi.b2c.emptyTitle', 'Aucun client B2C pour l’instant')}
          action={(
            <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={handleSync} disabled={isSyncing}>
              <CloudDownload aria-hidden="true" />
              {t('clientsPage.sync', 'Synchroniser depuis les tickets')}
            </button>
          )}
        >
          {t('clientsUi.b2c.emptyHint', 'Chaque personne qui vous a écrit devient un client : lancez une synchronisation depuis vos tickets.')}
        </EmptyState>
      );
    }
    return (
      <div className="u-table-scroll">
        <table className="u-table customers-v2__table">
          <thead>
            <tr>
              <th style={{ width: '26%' }}>{t('clientsUi.b2c.colClient', 'Client')}</th>
              <th style={{ width: '17%' }}>{t('clientsUi.b2c.colPhone', 'Téléphone')}</th>
              <th style={{ width: '21%' }}>{t('clientsUi.b2c.colAddress', 'Adresse')}</th>
              <th style={{ width: '20%' }}>{t('clientsPage.source', 'Source')}</th>
              <th style={{ width: '11%' }}>{t('clientsUi.b2c.colUpdated', 'Mis à jour')}</th>
              <th style={{ width: '62px' }} aria-label={t('clientsUi.open', 'Ouvrir')} />
            </tr>
          </thead>
          <tbody>
            {displayed.map((client) => {
              const address = addressText(client.address);
              return (
                <tr
                  key={client.id}
                  className="customers-v2__row"
                  tabIndex={0}
                  onClick={() => openClient(client)}
                  onKeyDown={(e) => { if (e.key === 'Enter') openClient(client); }}
                >
                  <td title={[client.name, client.email].filter(Boolean).join(' · ')}>
                    <span className="customers-v2__name">
                      <span className="customers-v2__name-text">{client.name || <span className="u-muted">—</span>}</span>
                      {client.is_vip && (
                        <span className="customers-v2__vip" title={t('clientsPage.vip', 'VIP')}>
                          <Badge tone="warning" icon={Crown}>{t('clientsPage.vip', 'VIP')}</Badge>
                        </span>
                      )}
                    </span>
                    <span className="u-cell-sub">{client.email || t('clientsUi.b2c.noEmail', 'pas d’e-mail')}</span>
                  </td>
                  <td className="u-num">{client.phone_number || <span className="u-muted">—</span>}</td>
                  <td title={address}>
                    {address ? (
                      <span className="customers-v2__address">
                        <span className="customers-v2__address-text">{address}</span>
                        {addressNeedsReview(client) && <Badge tone="warning" dot>{t('clientsUi.b2c.toCheck', 'À vérifier')}</Badge>}
                      </span>
                    ) : <span className="u-muted">—</span>}
                  </td>
                  <td>
                    {client.source ? <span className="u-tag customers-v2__source">{sourceLabel(t, client.source)}</span> : <span className="u-muted">—</span>}
                    {client.created_at && (
                      <span className="u-cell-sub" title={t('clientsUi.b2c.since', 'client depuis le {{date}}', { date: formatShortDay(client.created_at) })}>
                        {t('clientsUi.list.since', 'depuis le {{date}}', { date: formatShortDay(client.created_at) })}
                      </span>
                    )}
                  </td>
                  <td className="u-num" title={formatStamp(client.updated_at)}>{formatShortDay(client.updated_at) || <span className="u-muted">—</span>}</td>
                  <td className="customers-v2__chevron"><ChevronRight aria-hidden="true" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="u-page customers-v2">
      <PageHeader
        title={t('clientsPage.titleB2C', 'Clients B2C')}
        subtitle={subtitle}
        actions={(
          <>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--icon"
              onClick={reload}
              disabled={loading || isSyncing}
              title={t('clientsPage.refresh', 'Actualiser')}
              aria-label={t('clientsPage.refresh', 'Actualiser')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            <button
              type="button"
              className="u-btn u-btn--primary"
              onClick={handleSync}
              disabled={isSyncing}
              title={t('clientsUi.b2c.syncHint', 'Crée un client pour chaque adresse qui vous a écrit (jusqu’à {{count}} tickets lus).', { count: formatCount(SYNC_LIMIT) })}
            >
              <CloudDownload aria-hidden="true" />
              {isSyncing ? t('clientsPage.syncing', 'Synchronisation…') : t('clientsPage.sync', 'Synchroniser depuis les tickets')}
            </button>
          </>
        )}
      />

      <div className="u-well">
        {error && (
          <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={reload}>{t('clientsPage.retry', 'Réessayer')}</button>}>
            {error}
          </Banner>
        )}
        {renderAttention()}
        {renderKpis()}
        <section className="u-card">
          <div className="u-toolbar">
            <div className="u-input-group u-input-group--lead">
              <Search className="u-input-group__icon" aria-hidden="true" />
              <input
                type="search"
                className="u-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('clientsUi.b2c.search', 'Nom, e-mail ou téléphone')}
                aria-label={t('common.search', 'Rechercher')}
              />
            </div>
            <Segmented
              label={t('clientsUi.b2c.filterLabel', 'Filtrer la page affichée')}
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: t('clientsUi.filters.all', 'Tous'), count: loading ? undefined : formatCount(summary.shown) },
                { value: 'vip', label: t('clientsPage.vip', 'VIP'), count: loading ? undefined : formatCount(summary.vip) },
                { value: 'address', label: t('clientsUi.b2c.filterAddress', 'Adresse à vérifier'), count: loading ? undefined : formatCount(summary.needsReview) },
              ]}
            />
            <span className="u-toolbar__spacer" />
            {!loading && filter !== 'all' && (
              <span className="customers-v2__scope">{t('clientsPage.vipFilterHint', 'Filtre appliqué à la page affichée')}</span>
            )}
          </div>
          {renderTable()}
          {!loading && (page > 1 || hasMore || list.length > 0) && (
            <div className="u-pagination">
              <span className="customers-v2__pager-summary">
                {t('clientsUi.pageN', 'Page {{page}}', { page })}
                <select
                  className="u-select customers-v2__size"
                  value={pageSize}
                  onChange={(e) => updateParams({ size: Number(e.target.value) === 50 ? null : e.target.value, page: null })}
                  aria-label={t('clientsUi.perPage', 'Lignes par page')}
                >
                  {PAGE_SIZES.map((size) => <option key={size} value={size}>{t('clientsUi.perPageOption', '{{count}} par page', { count: size })}</option>)}
                </select>
              </span>
              <div className="u-pagination__pages">
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={page <= 1} onClick={() => updateParams({ page: page - 1 <= 1 ? null : page - 1 })}>
                  {t('clientsPage.previous', 'Précédent')}
                </button>
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={!hasMore} onClick={() => updateParams({ page: page + 1 })}>
                  {t('clientsPage.next', 'Suivant')}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')} persistent>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default Clients;
